import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  LinearProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import {
  Favorite as HeartIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { MatchingResult, matchingApi } from '../services/api';
import PlacesAutocomplete from '../components/PlacesAutocomplete';

const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));
const MERIDIEMS = ['AM', 'PM'] as const;

interface PersonForm {
  name: string;
  dob: Dayjs | null;
  hour: string;
  minute: string;
  meridiem: 'AM' | 'PM';
  place: string;
}

const emptyPerson = (): PersonForm => ({
  name: '', dob: null, hour: '', minute: '', meridiem: 'AM', place: '',
});

const formatTime = (p: PersonForm): string => {
  let h = parseInt(p.hour, 10);
  if (p.meridiem === 'PM' && h !== 12) h += 12;
  if (p.meridiem === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${p.minute}`;
};

const isFormValid = (p: PersonForm): boolean =>
  p.name.trim().length >= 2 && p.dob !== null && p.hour !== '' && p.minute !== '' && p.place.trim().length >= 2;

const PersonFormFields: React.FC<{
  label: string;
  color: string;
  person: PersonForm;
  onChange: (p: PersonForm) => void;
}> = ({ label, color, person, onChange }) => (
  <Card sx={{ borderTop: `4px solid ${color}`, height: '100%' }}>
    <CardContent>
      <Typography variant="h6" sx={{ fontWeight: 700, color, mb: 2 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Full Name"
          value={person.name}
          onChange={(e) => onChange({ ...person, name: e.target.value })}
          fullWidth
          size="small"
        />
        <DatePicker
          label="Date of Birth"
          value={person.dob}
          onChange={(v) => onChange({ ...person, dob: v })}
          maxDate={dayjs()}
          slotProps={{ textField: { fullWidth: true, size: 'small' } }}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            select label="Hr" value={person.hour} size="small"
            onChange={(e) => onChange({ ...person, hour: e.target.value })}
            sx={{ flex: 1 }}
          >
            {HOURS.map((h) => <MenuItem key={h} value={h}>{h}</MenuItem>)}
          </TextField>
          <TextField
            select label="Min" value={person.minute} size="small"
            onChange={(e) => onChange({ ...person, minute: e.target.value })}
            sx={{ flex: 1 }}
          >
            {MINUTES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
          </TextField>
          <TextField
            select label="AM/PM" value={person.meridiem} size="small"
            onChange={(e) => onChange({ ...person, meridiem: e.target.value as 'AM' | 'PM' })}
            sx={{ flex: 1 }}
          >
            {MERIDIEMS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
          </TextField>
        </Box>
        <PlacesAutocomplete
          value={person.place}
          onChange={(val) => onChange({ ...person, place: val })}
          size="small"
          placeholder="e.g. Hyderabad, India"
        />
      </Box>
    </CardContent>
  </Card>
);

const KundaliMatching: React.FC = () => {
  const [boy, setBoy] = useState<PersonForm>(emptyPerson());
  const [girl, setGirl] = useState<PersonForm>(emptyPerson());
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<MatchingResult | null>(null);
  const [error, setError] = useState('');

  const canSubmit = isFormValid(boy) && isFormValid(girl) && !loading;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await matchingApi.calculate({
        boy_name: boy.name.trim(),
        boy_dob: boy.dob!.format('YYYY-MM-DD'),
        boy_tob: formatTime(boy),
        boy_pob: boy.place.trim(),
        girl_name: girl.name.trim(),
        girl_dob: girl.dob!.format('YYYY-MM-DD'),
        girl_tob: formatTime(girl),
        girl_pob: girl.place.trim(),
      });
      setResult(res);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to calculate compatibility. Please check details and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setBoy(emptyPerson());
    setGirl(emptyPerson());
    setResult(null);
    setError('');
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 5 } }}>
        <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, fontSize: { xs: '1.6rem', sm: '2rem', md: '2.5rem' } }}>
          గుణ మిలానం — Kundali Matching
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          Ashtakoot Gun Milan — traditional Vedic compatibility analysis based on birth charts
        </Typography>
      </Box>

      {/* Input Forms */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <PersonFormFields label="🤵 Groom Details" color="#1565C0" person={boy} onChange={setBoy} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <PersonFormFields label="👰 Bride Details" color="#AD1457" person={girl} onChange={setGirl} />
        </Grid>
      </Grid>

      <Box sx={{ textAlign: 'center', mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'center', alignItems: 'center', gap: 2 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <HeartIcon />}
          sx={{ px: 5, py: 1.5, fontSize: '1rem', width: { xs: '100%', sm: 'auto' } }}
        >
          {loading ? 'Calculating...' : 'Check Compatibility'}
        </Button>
        {(result || error) && (
          <Button variant="outlined" size="large" onClick={handleReset} sx={{ py: 1.5, width: { xs: '100%', sm: 'auto' } }}>
            Reset
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Results */}
      {result && (
        <Box>
          {/* Score Overview */}
          <Card sx={{ mb: 3, border: `2px solid ${result.verdict.color}` }}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h2" sx={{ fontWeight: 800, color: result.verdict.color, mb: 1, fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } }}>
                {result.totalScore} / {result.maxScore}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={result.percentage}
                sx={{
                  height: 12,
                  borderRadius: 6,
                  maxWidth: 400,
                  mx: 'auto',
                  mb: 2,
                  bgcolor: '#eee',
                  '& .MuiLinearProgress-bar': { bgcolor: result.verdict.color, borderRadius: 6 },
                }}
              />
              <Chip
                label={`${result.verdict.level} (${result.verdict.levelTelugu})`}
                sx={{ bgcolor: result.verdict.color, color: '#fff', fontWeight: 700, fontSize: { xs: '0.85rem', md: '1rem' }, px: 2, py: 2.5, mb: 1 }}
              />
              <Typography variant="body1" sx={{ color: 'text.secondary', mt: 2, maxWidth: 600, mx: 'auto' }}>
                {result.verdict.message}
              </Typography>
            </CardContent>
          </Card>

          {/* Person Details */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Groom', data: result.boy, color: '#1565C0' },
              { label: 'Bride', data: result.girl, color: '#AD1457' },
            ].map(({ label, data, color }) => (
              <Grid size={{ xs: 12, sm: 6 }} key={label}>
                <Card sx={{ borderTop: `3px solid ${color}` }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 700, color, mb: 1 }}>{label}: {data.name}</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Chip label={`Rashi: ${data.rashi} (${data.rashiTelugu})`} size="small" variant="outlined" />
                      <Chip label={`Nakshatra: ${data.nakshatra} (Pada ${data.nakshatraPada})`} size="small" variant="outlined" />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Score Breakdown */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Ashtakoot Score Breakdown
              </Typography>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 500 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'primary.main' }}>
                      <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Koota</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700 }}>తెలుగు</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Aspect</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Score</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Max</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.scores.map((s) => (
                      <TableRow key={s.name} sx={{ '&:nth-of-type(odd)': { bgcolor: '#faf5ef' } }}>
                        <TableCell sx={{ fontWeight: 600 }}>{s.name}</TableCell>
                        <TableCell>{s.nameTelugu}</TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>{s.description}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: '1rem' }}>{s.obtained}</TableCell>
                        <TableCell align="center">{s.maxScore}</TableCell>
                        <TableCell align="center">
                          {s.obtained >= s.maxScore * 0.5 ? (
                            <CheckIcon sx={{ color: '#2E7D32' }} />
                          ) : (
                            <CancelIcon sx={{ color: '#C62828' }} />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: '#f5f0eb' }}>
                      <TableCell colSpan={3} sx={{ fontWeight: 700, fontSize: '1rem' }}>Total</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 800, fontSize: '1.1rem', color: result.verdict.color }}>
                        {result.totalScore}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>{result.maxScore}</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Disclaimer */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">{result.disclaimer}</Typography>
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
              For a complete chart analysis,{' '}
              <Typography component="a" href="/book-slot" variant="body2" sx={{ color: 'primary.main', fontWeight: 700 }}>
                book a consultation
              </Typography>{' '}
              with our astrologer.
            </Typography>
          </Alert>
        </Box>
      )}
    </Container>
  );
};

export default KundaliMatching;
